import React, { useState, useEffect } from 'react';
import { Settings, Building2, DollarSign, Bell, Server, Info, CheckCircle, XCircle, X } from 'lucide-react';
import apiService from '../services/api';

// Componentes de pestañas
import TabGeneral from './Ajustes/TabGeneral';
import TabNegocio from './Ajustes/TabNegocio';
import TabAlertas from './Ajustes/TabAlertas';
import TabSistema from './Ajustes/TabSistema';
import TabInfo from './Ajustes/TabInfo';

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const duration = 4000;
    const interval = 10;
    const decrement = (100 * interval) / duration;

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(progressTimer);
          return 0;
        }
        return prev - decrement;
      });
    }, interval);

    const closeTimer = setTimeout(() => onClose(), duration);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(closeTimer);
    };
  }, [onClose]);

  const config = {
    success: {
      bgColor: 'bg-white border border-green-200 shadow-soft',
      progressColor: 'bg-green-500',
      textColor: 'text-green-800',
      icon: <CheckCircle size={22} />
    },
    error: {
      bgColor: 'bg-white border border-red-200 shadow-soft',
      progressColor: 'bg-red-500',
      textColor: 'text-red-800',
      icon: <XCircle size={22} />
    }
  };

  const currentConfig = config[type] || config.success;

  return (
    <div className={`${currentConfig.bgColor} ${currentConfig.textColor} rounded-xl overflow-hidden min-w-[320px] max-w-md animate-slideIn`}>
      <div className="px-5 py-4 flex items-start gap-3">
        <div className="flex-shrink-0">{currentConfig.icon}</div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm leading-relaxed break-words">{message}</p>
        </div>
        <button onClick={onClose} className="flex-shrink-0 hover:bg-ink-100 rounded-lg p-1.5 transition-all duration-200 ml-2">
          <X size={18} />
        </button>
      </div>
      <div className="h-1 bg-ink-100">
        <div className={`h-full ${currentConfig.progressColor} transition-all duration-100 ease-linear`} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

function Ajustes() {
  console.log('🚀 AJUSTES NUEVO CARGADO - Versión con Pestañas');

  const [activeTab, setActiveTab] = useState('general');
  const [configuracion, setConfiguracion] = useState({});
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const tabs = [
    { id: 'general', name: 'General', icon: Building2, color: 'blue' },
    { id: 'negocio', name: 'Negocio', icon: DollarSign, color: 'green' },
    { id: 'alertas', name: 'Alertas', icon: Bell, color: 'orange' },
    { id: 'sistema', name: 'Sistema', icon: Server, color: 'purple' },
    { id: 'info', name: 'Información', icon: Info, color: 'gray' }
  ];

  useEffect(() => {
    fetchConfiguracion();
  }, []);

  const fetchConfiguracion = async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/configuracion');
      setConfiguracion(data);
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast('Error al cargar configuración', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleSave = async (updates) => {
    try {
      await apiService.put('/configuracion', updates);
      showToast('Configuración guardada exitosamente', 'success');
      setHasChanges(false);
      await fetchConfiguracion();
      // Notificar a App.js y NotificacionesContext que la config cambió
      window.dispatchEvent(new CustomEvent('config-updated', { detail: updates }));
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast(error.message || 'Error al guardar configuración', 'error');
      }
    }
  };

  const renderTab = () => {
    const commonProps = {
      configuracion,
      onSave: handleSave,
      showToast,
      setHasChanges
    };

    switch (activeTab) {
      case 'general':
        return <TabGeneral {...commonProps} />;
      case 'negocio':
        return <TabNegocio {...commonProps} />;
      case 'alertas':
        return <TabAlertas {...commonProps} />;
      case 'sistema':
        return <TabSistema {...commonProps} />;
      case 'info':
        return <TabInfo configuracion={configuracion} />;
      default:
        return <TabGeneral {...commonProps} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-ink-200 border-t-ink-800 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-ink-400 tracking-wide">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-ink-50 min-h-screen">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-3">
        {toasts.map((toast) => (
          <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
        ))}
      </div>

      {/* Header */}
      <div className="mb-8 animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <Settings size={32} className="text-ink-900" />
          <h1 className="font-display text-3xl text-ink-900">Ajustes del Sistema</h1>
        </div>
        <p className="text-ink-500">Configuración global y preferencias del sistema</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-soft border border-ink-200 overflow-hidden">
        {/* Tab Headers */}
        <div className="border-b border-ink-200 bg-ink-50">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-6 py-4 font-medium text-sm whitespace-nowrap
                    border-b-2 transition-all duration-200
                    ${isActive
                      ? `border-ink-900 text-ink-900 bg-white`
                      : 'border-transparent text-ink-500 hover:text-ink-700 hover:bg-ink-100'
                    }
                  `}
                >
                  <Icon size={18} className={isActive ? 'text-ink-900' : 'text-ink-400'} />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTab()}
        </div>
      </div>

      {/* Cambios sin guardar */}
      {hasChanges && (
        <div className="fixed bottom-8 left-8 bg-yellow-50 border-2 border-yellow-300 rounded-lg shadow-lifted p-4 animate-slideIn">
          <p className="text-sm text-yellow-800 font-medium">
            ⚠️ Tienes cambios sin guardar
          </p>
        </div>
      )}
    </div>
  );
}

export default Ajustes;
